/**
 * src/components/spatial/MapDataContext.tsx
 *
 * Normalised, framework-native state model for the GeoTrust deck.gl map
 * ("GeoTrustMapState"). This is the single source of truth the deck
 * layer registry reads from, and the sink that data fetchers + realtime
 * deltas write into.
 *
 * Phase 1 populates only `spatialObjects` + selection/hover + status.
 * Later phases fill the remaining slices (countries, executionRules,
 * ruleEvents, agents, agentPaths, liveSessions, presenceCells, drones,
 * transactions) without changing the surrounding wiring — the registry
 * and overlay already iterate `visibleLayerIds`.
 *
 * Intentionally a small reducer + Context (the app has no Redux/Zustand
 * and uses Context for the sibling `SpatialObjectsContext`). Kept
 * additive so it can sit alongside App.tsx's existing useState state
 * without a broad refactor.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { SpatialObjectFeature } from '../../services/spatial/geoLinkTypes';
import type { MapFeature } from '../../services/spatial/map/mapTypes';
import { spatialFeatureId } from './spatialSemantics';

export type MapEntityType =
  | 'spatialObject'
  | 'country'
  | 'executionRule'
  | 'ruleEvent'
  | 'agent'
  | 'liveSession'
  | 'presenceCell'
  | 'drone'
  | 'transaction';

export interface SelectedEntity {
  entityType: MapEntityType;
  id: string;
  /** The full feature backing the selection (spatial objects today). */
  feature?: SpatialObjectFeature | null;
  /** The aggregate map-API feature backing a non-spatial-object selection. */
  entityFeature?: MapFeature | null;
  /** Pixel anchor for the floating detail panel, when selection came from a click. */
  anchor?: { x: number; y: number };
}

export interface HoveredEntity {
  entityType: MapEntityType;
  id: string;
}

export interface MapDataFilters {
  countryCode?: string | null;
  objectType?: string | null;
  status?: string | null;
  verifiedOnly?: boolean;
  activeRuleOnly?: boolean;
}

export interface MapDataStatus {
  loading: boolean;
  error: string | null;
  /** True when GeoLink returned 401/403 for the current identity/scope. */
  permissionDenied: boolean;
  /** True when the last successful fetch returned zero features. */
  empty: boolean;
}

export type PlaybackState = 'stopped' | 'playing' | 'paused';

/** Names of the normalized map-API slices (Phase 2). */
export type MapSliceKey =
  | 'countries'
  | 'executionRules'
  | 'ruleEvents'
  | 'agents'
  | 'agentPaths'
  | 'liveSessions'
  | 'presenceCells'
  | 'drones'
  | 'transactions';

export interface SlicePagination {
  nextCursor?: string | null;
  hasMore?: boolean;
  /** Latest `updatedAt` seen — used as `updatedSince` on the next fetch. */
  lastUpdatedAt?: string | null;
}

export interface GeoTrustMapState {
  // ── Entity slices ──
  // spatialObjects keeps its Phase-1 type (fed by the render envelope or
  // the map-API cutover); the rest are normalized /api/map/* features.
  spatialObjects: SpatialObjectFeature[];
  countries: MapFeature[];
  executionRules: MapFeature[];
  ruleEvents: MapFeature[];
  agents: MapFeature[];
  agentPaths: MapFeature[];
  liveSessions: MapFeature[];
  presenceCells: MapFeature[];
  drones: MapFeature[];
  transactions: MapFeature[];

  // ── Interaction ──
  selectedEntity: SelectedEntity | null;
  hoveredEntity: HoveredEntity | null;

  // ── Presentation ──
  visibleLayerIds: string[];
  filters: MapDataFilters;
  currentTime: number | null;
  playbackState: PlaybackState;

  // ── Fetch lifecycle ──
  /** Aggregate status (Phase 1 spatial path + overlay badge). */
  status: MapDataStatus;
  /** Per-layer status so one restricted layer can't blank the whole map. */
  layerStatus: Record<string, MapDataStatus>;
  /** Per-slice pagination + incremental-fetch cursors. */
  pagination: Partial<Record<MapSliceKey, SlicePagination>>;
}

const initialState: GeoTrustMapState = {
  spatialObjects: [],
  countries: [],
  executionRules: [],
  ruleEvents: [],
  agents: [],
  agentPaths: [],
  liveSessions: [],
  presenceCells: [],
  drones: [],
  transactions: [],
  selectedEntity: null,
  hoveredEntity: null,
  visibleLayerIds: ['geotrust-spatial-objects'],
  filters: {},
  currentTime: null,
  playbackState: 'stopped',
  status: { loading: false, error: null, permissionDenied: false, empty: false },
  layerStatus: {},
  pagination: {},
};

/** Stable id for any map feature (feature id → properties.id). */
function mapFeatureId(f: MapFeature): string {
  if (f?.id != null) return String(f.id);
  const pid = f?.properties?.id;
  return pid != null ? String(pid) : '';
}

/** Upsert `upserts` (by id) into `list` and drop any `removeIds`. */
function upsertById(list: MapFeature[], upserts?: MapFeature[], removeIds?: string[]): MapFeature[] {
  const byId = new Map<string, MapFeature>();
  for (const f of list) byId.set(mapFeatureId(f), f);
  if (upserts) for (const f of upserts) byId.set(mapFeatureId(f), f);
  if (removeIds) for (const id of removeIds) byId.delete(String(id));
  return Array.from(byId.values());
}

type Action =
  | { type: 'SET_SPATIAL_OBJECTS'; features: SpatialObjectFeature[] }
  | { type: 'SET_SLICE'; slice: MapSliceKey; features: MapFeature[]; pagination?: SlicePagination }
  | { type: 'APPLY_DELTA'; slice: MapSliceKey; upserts?: MapFeature[]; removeIds?: string[] }
  | { type: 'SELECT_ENTITY'; entity: SelectedEntity | null }
  | { type: 'HOVER_ENTITY'; entity: HoveredEntity | null }
  | { type: 'SET_VISIBLE_LAYERS'; layerIds: string[] }
  | { type: 'TOGGLE_LAYER'; layerId: string; visible: boolean }
  | { type: 'SET_FILTERS'; filters: Partial<MapDataFilters> }
  | { type: 'SET_TIME'; time: number | null }
  | { type: 'SET_PLAYBACK'; playback: PlaybackState }
  | { type: 'SET_STATUS'; status: Partial<MapDataStatus> }
  | { type: 'SET_LAYER_STATUS'; layerId: string; status: Partial<MapDataStatus> };

function reducer(state: GeoTrustMapState, action: Action): GeoTrustMapState {
  switch (action.type) {
    case 'SET_SPATIAL_OBJECTS':
      return {
        ...state,
        spatialObjects: action.features,
        status: { ...state.status, empty: action.features.length === 0 },
      };
    case 'SET_SLICE':
      return {
        ...state,
        [action.slice]: action.features,
        pagination: action.pagination
          ? { ...state.pagination, [action.slice]: action.pagination }
          : state.pagination,
      } as GeoTrustMapState;
    case 'APPLY_DELTA': {
      const current = (state[action.slice] as MapFeature[]) ?? [];
      return {
        ...state,
        [action.slice]: upsertById(current, action.upserts, action.removeIds),
      } as GeoTrustMapState;
    }
    case 'SET_LAYER_STATUS':
      return {
        ...state,
        layerStatus: {
          ...state.layerStatus,
          [action.layerId]: {
            ...(state.layerStatus[action.layerId] ?? { loading: false, error: null, permissionDenied: false, empty: false }),
            ...action.status,
          },
        },
      };
    case 'SELECT_ENTITY':
      return { ...state, selectedEntity: action.entity };
    case 'HOVER_ENTITY': {
      const prev = state.hoveredEntity;
      const next = action.entity;
      // Avoid churn: same hovered id → no state change (keeps deck
      // updateTriggers stable during mousemove).
      if (prev?.id === next?.id && prev?.entityType === next?.entityType) return state;
      return { ...state, hoveredEntity: next };
    }
    case 'SET_VISIBLE_LAYERS':
      return { ...state, visibleLayerIds: action.layerIds };
    case 'TOGGLE_LAYER': {
      const set = new Set(state.visibleLayerIds);
      if (action.visible) set.add(action.layerId);
      else set.delete(action.layerId);
      return { ...state, visibleLayerIds: Array.from(set) };
    }
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.filters } };
    case 'SET_TIME':
      return { ...state, currentTime: action.time };
    case 'SET_PLAYBACK':
      return { ...state, playbackState: action.playback };
    case 'SET_STATUS':
      return { ...state, status: { ...state.status, ...action.status } };
    default:
      return state;
  }
}

export interface MapDataContextValue {
  state: GeoTrustMapState;
  setSpatialObjects(features: SpatialObjectFeature[]): void;
  setSlice(slice: MapSliceKey, features: MapFeature[], pagination?: SlicePagination): void;
  applyDelta(slice: MapSliceKey, upserts?: MapFeature[], removeIds?: string[]): void;
  setLayerStatus(layerId: string, status: Partial<MapDataStatus>): void;
  selectEntity(entity: SelectedEntity | null): void;
  selectSpatialObject(feature: SpatialObjectFeature | null, anchor?: { x: number; y: number }): void;
  hoverEntity(entity: HoveredEntity | null): void;
  hoverSpatialObject(feature: SpatialObjectFeature | null): void;
  setVisibleLayers(layerIds: string[]): void;
  toggleLayer(layerId: string, visible: boolean): void;
  setFilters(filters: Partial<MapDataFilters>): void;
  setTime(time: number | null): void;
  setPlayback(playback: PlaybackState): void;
  setStatus(status: Partial<MapDataStatus>): void;
}

const MapDataContext = createContext<MapDataContextValue | null>(null);

export const MapDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSpatialObjects = useCallback((features: SpatialObjectFeature[]) => {
    dispatch({ type: 'SET_SPATIAL_OBJECTS', features });
  }, []);

  const setSlice = useCallback((slice: MapSliceKey, features: MapFeature[], pagination?: SlicePagination) => {
    dispatch({ type: 'SET_SLICE', slice, features, pagination });
  }, []);

  const applyDelta = useCallback((slice: MapSliceKey, upserts?: MapFeature[], removeIds?: string[]) => {
    dispatch({ type: 'APPLY_DELTA', slice, upserts, removeIds });
  }, []);

  const setLayerStatus = useCallback((layerId: string, status: Partial<MapDataStatus>) => {
    dispatch({ type: 'SET_LAYER_STATUS', layerId, status });
  }, []);

  const selectEntity = useCallback((entity: SelectedEntity | null) => {
    dispatch({ type: 'SELECT_ENTITY', entity });
  }, []);

  const selectSpatialObject = useCallback((feature: SpatialObjectFeature | null, anchor?: { x: number; y: number }) => {
    if (!feature) {
      dispatch({ type: 'SELECT_ENTITY', entity: null });
      return;
    }
    dispatch({
      type: 'SELECT_ENTITY',
      entity: { entityType: 'spatialObject', id: spatialFeatureId(feature), feature, anchor },
    });
  }, []);

  const hoverEntity = useCallback((entity: HoveredEntity | null) => {
    dispatch({ type: 'HOVER_ENTITY', entity });
  }, []);

  const hoverSpatialObject = useCallback((feature: SpatialObjectFeature | null) => {
    dispatch({
      type: 'HOVER_ENTITY',
      entity: feature ? { entityType: 'spatialObject', id: spatialFeatureId(feature) } : null,
    });
  }, []);

  const setVisibleLayers = useCallback((layerIds: string[]) => dispatch({ type: 'SET_VISIBLE_LAYERS', layerIds }), []);
  const toggleLayer = useCallback((layerId: string, visible: boolean) => dispatch({ type: 'TOGGLE_LAYER', layerId, visible }), []);
  const setFilters = useCallback((filters: Partial<MapDataFilters>) => dispatch({ type: 'SET_FILTERS', filters }), []);
  const setTime = useCallback((time: number | null) => dispatch({ type: 'SET_TIME', time }), []);
  const setPlayback = useCallback((playback: PlaybackState) => dispatch({ type: 'SET_PLAYBACK', playback }), []);
  const setStatus = useCallback((status: Partial<MapDataStatus>) => dispatch({ type: 'SET_STATUS', status }), []);

  const value = useMemo<MapDataContextValue>(() => ({
    state,
    setSpatialObjects,
    setSlice,
    applyDelta,
    setLayerStatus,
    selectEntity,
    selectSpatialObject,
    hoverEntity,
    hoverSpatialObject,
    setVisibleLayers,
    toggleLayer,
    setFilters,
    setTime,
    setPlayback,
    setStatus,
  }), [
    state, setSpatialObjects, setSlice, applyDelta, setLayerStatus,
    selectEntity, selectSpatialObject, hoverEntity,
    hoverSpatialObject, setVisibleLayers, toggleLayer, setFilters, setTime,
    setPlayback, setStatus,
  ]);

  return <MapDataContext.Provider value={value}>{children}</MapDataContext.Provider>;
};

export function useMapData(): MapDataContextValue {
  const ctx = useContext(MapDataContext);
  if (!ctx) throw new Error('useMapData must be used within <MapDataProvider>');
  return ctx;
}

/** Optional variant — returns null outside a provider (safe for legacy paths). */
export function useMapDataOptional(): MapDataContextValue | null {
  return useContext(MapDataContext);
}

export { initialState as mapDataInitialState, reducer as mapDataReducer };
